import { Container, Section } from '@resolute/ui';

import { Reveal } from './motion/reveal';

export interface PageHeroProps {
  /** Short uppercase category label shown above the heading (e.g. "Legal"). */
  eyebrow: string;
  title: string;
  /** Optional second heading line, rendered muted below `title` — matches the
   *  About page's two-line "statement" heading style (e.g. "We Didn't List
   *  Properties." / "We Verified Them First."). Omit for a single-line title. */
  titleLine2?: string;
  description?: string;
  /** Optional trailing meta line (e.g. "Last updated: July 2026"). */
  meta?: string;
}

/**
 * Shared hero band for every static/content page (Contact, legal pages, FAQ,
 * Vaastu Tips, tool/placeholder stubs) — one consistent eyebrow + heading +
 * subtitle treatment instead of each page hand-rolling its own. Matches the
 * About page's hero proportions (full-width container, side-by-side
 * heading/description on large screens, display-md heading) so every static
 * page's header reads as one family — About just adds its own stats band
 * below this same shared markup, it isn't a different hero.
 */
export function PageHero({ eyebrow, title, titleLine2, description, meta }: PageHeroProps) {
  return (
    <Section spacing="lg" className="border-b border-border bg-muted/30">
      <Container>
        <Reveal>
          <p className="text-sm font-medium uppercase tracking-widest text-secondary">{eyebrow}</p>
        </Reveal>

        <div className="mt-4 grid gap-8 lg:grid-cols-[3fr_2fr] lg:items-end lg:gap-12">
          <Reveal>
            <h1 className="text-balance font-display text-display-md font-semibold tracking-tight">
              {title}
              {titleLine2 && <span className="block text-muted-foreground">{titleLine2}</span>}
            </h1>
          </Reveal>
          {description && (
            <Reveal delay={0.08}>
              <p className="text-pretty text-lg text-muted-foreground lg:pb-2">{description}</p>
            </Reveal>
          )}
        </div>

        {meta && (
          <Reveal delay={0.14}>
            <p className="mt-4 text-sm text-muted-foreground/70">{meta}</p>
          </Reveal>
        )}
      </Container>
    </Section>
  );
}
